import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { EngineService } from '@core/services/engine/engine.service';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SaveSessionBlindsService } from '@core/services/save-session-blinds/save-session-blinds.service';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { BREAKPOINTS, STORAGE_NAMES, SELECTORS, VIEW_TYPES, CLASSES } from '@root/app.config';
import { Breakpoints, Retailer } from '@root/app.interfaces';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { RetailerInfoModalComponent } from '@shared/modals/retailer-info-modal/retailer-info-modal.component';
import { SaveWorkEmailModalComponent } from '@shared/modals/save-work-email-modal/save-work-email-modal.component';
import { ModalService } from '@core/services/modal/modal.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_OVERVIEW_EVENTS } from '@core/services/gtm/gtm.events';
import { VariantService } from '@core/services/variant/variant.service';

import * as _ from 'lodash';
import { QuizService } from '@core/services/quiz/quiz.service';
import { ServerDataService } from '@core/services/server-data/server-data.service';

@Component({
	selector: 'app-summary-table',
	templateUrl: './summary-table.component.html',
	styleUrls: [
		'./summary-table.component.scss',
		'./summary-table.responsive.scss',
		'../overview-page/overview-page.component.scss',
		'../overview-page/overview-page.responsive.scss',
	],
	encapsulation: ViewEncapsulation.None,
})
export class SummaryTableComponent implements OnInit, OnDestroy {
	@ViewChild('requestBlockButtons') requestBlockButtons: ElementRef<HTMLDivElement>;
	@ViewChild('mobileRequestBlockButtons') mobileRequestBlockButtons: ElementRef<HTMLDivElement>;
	@ViewChild('gallery') gallery: ElementRef<HTMLElement>;

	protected readonly IS_SHOP = this.variant.isShop();

	getEmbeddedRetailer: Subscription;
	getSessionBLindData: Subscription;
	getActivatedRouteRules: Subscription;
	getScreenshotLoadedOnServer: Subscription;

	hasBlindsData = false;
	downloading = false;
	quotationLoading: boolean;
	isScreenshotLoadedOnServer: boolean;
	saving = false;
	breakpoints: Breakpoints = BREAKPOINTS;
	isMobile: boolean;
	isShop = this.variant.isShop();

	embeddedRetailer: Retailer = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer);
	storageText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);
	sessionText = _.isEmpty(this.storageText) ? 'Some text' : this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);

	@HostListener('window:scroll') onScroll() {
		this.stickyButtonsPanelHandler();
	}
	@HostListener('window:resize') onResize() {
		this.stickyButtonsPanelHandler();
	}

	constructor(
		private sessionStorageService: SessionStorageService,
		private saveSessionBlind: SaveSessionBlindsService,
		private countryCodeService: SetupPageService,
		private engineService: EngineService,
		private route: ActivatedRoute,
		private router: Router,
		private shareService: ShareService,
		private changeDetection: ChangeDetectorRef,
		private modalService: ModalService,
		private passDataService: PassDataService,
		public dialog: MatDialog,
		private gtm: GtmService,
		private variant: VariantService,
		private quizService: QuizService,
		private serverDataService: ServerDataService,
	) {}

	ngOnInit(): void {
		this.sceneHandler();
		this.isMobile = this.countryCodeService.mobileAndTabletCheck();
		this.getActivatedRouteRules = this.route.data.subscribe(() => {
			this.getSessionStorageData();
		});

		if (!this.hasBlindsData) {
			this.getSessionBLindData = this.shareService.getSessionBlindsData.subscribe(() => {
				this.getSessionDefaultValue();
				this.getSessionStorageData();
			});
		}

		this.isScreenshotLoadedOnServer = !_.isUndefined(this.passDataService.isScreenShotLoadedOnServer)
			? this.passDataService.isScreenShotLoadedOnServer
			: true;
		this.getScreenshotLoadedOnServer = this.shareService.getScreenShotLoadedOnServer.subscribe(
			(status: boolean) => (this.isScreenshotLoadedOnServer = status),
		);
		this.getEmbeddedRetailer = this.shareService.getEmbeddedRetailer.subscribe(() => {
			this.embeddedRetailer = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_embedded_retailer);
		});
	}

	ngOnDestroy(): void {
		if (this.getSessionBLindData) {
			this.getSessionBLindData.unsubscribe();
		}
		if (this.getActivatedRouteRules) {
			this.getActivatedRouteRules.unsubscribe();
		}

		this.getScreenshotLoadedOnServer.unsubscribe();
		this.getEmbeddedRetailer.unsubscribe();
	}

	getSessionStorageData(): void {
		const storageData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		this.hasBlindsData = !_.isEmpty(storageData);

		setTimeout(this.stickyButtonsPanelHandler.bind(this));
		this.changeDetection.markForCheck();
	}

	setBlindsTableData(): void {
		if (this.embeddedRetailer) {
			document.querySelector(SELECTORS.request_button).classList.add(CLASSES.loading);
		}

		this.shareService.setRetailerInfo(true);
	}

	putDownloadData(): void {
		this.downloading = true;
		this.saveSessionBlind.putDownloadDataPromise().then(() => {
			this.downloading = false;
			this.changeDetection.markForCheck();
		});
		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.DOWNLOAD_PDF);
	}

	putSaveBlinds(): void {
		this.modalService.openResponsiveDialog({
			...{ component: SaveWorkEmailModalComponent },
			...this.modalService.getConfig(SaveWorkEmailModalComponent.ID),
		});
		this.gtm.pushEvent(GTM_OVERVIEW_EVENTS.SAVE_WORK);
	}

	getSessionDefaultValue(): void {
		const sessionStorageText = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_text);

		if (!_.isEmpty(sessionStorageText)) {
			this.sessionText = sessionStorageText;
		}
	}

	onOpenRetailerInfoModal() {
		this.modalService.openResponsiveDialog({
			component: RetailerInfoModalComponent,
			width: '100%',
			maxWidth: '280px',
			panelClass: '',
		});
	}

	toHomePage(): void {
		this.router.navigateByUrl('home').then(() => {
			this.engineService.setBackgroundImage(null);
			if (this.sessionStorageService.getSession(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.design) {
				this.quizService.showQuizIfNeeded(VIEW_TYPES.design);
			}
		});
	}

	sceneHandler(): void {
		const getViewType = this.sessionStorageService.getSession(STORAGE_NAMES.zip_view_type);
		this.imageVisualisationStatusHandler(getViewType === VIEW_TYPES.image_visualisation || !getViewType);

		this.shareService.setTaskbarPanel(false);
		this.engineService.isModelCreated = false;
	}

	imageVisualisationStatusHandler(status: boolean): void {
		if (status) {
			this.sessionStorageService.setSession(true, STORAGE_NAMES.zip_image_visualisation_status);
		} else {
			this.sessionStorageService.removeData(STORAGE_NAMES.zip_image_visualisation_status);
		}
	}

	stickyButtonsPanelHandler(): void {
		const element = this.requestBlockButtons?.nativeElement.getBoundingClientRect().top || 0;
		const mobileElement = this.mobileRequestBlockButtons?.nativeElement.getBoundingClientRect().top || 0;
		const summaryButtonsTop = mobileElement || element;
		const buttonsInViewport = window.innerHeight >= summaryButtonsTop + 64;

		document.querySelector(SELECTORS.overview_bottom_panel).classList.toggle('hidden', buttonsInViewport);
		document.querySelector(SELECTORS.top_button).classList.toggle('offsetted', !buttonsInViewport);
	}

	onQuotationPageNavigate(): void {
		const sessionSaved = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_session_saved);

		if (!sessionSaved) {
			this.quotationLoading = true;
			this.saveSessionBlind.saveSessionOnNavigateToQuotation().then(() => {
				if (this.passDataService.isShopFormVerified) {
					this.getPrice();
				} else {
					this.navigateToQuotationPage();
				}
			});
		} else if (this.passDataService.isShopFormVerified) {
			this.getPrice();
		} else {
			this.navigateToQuotationPage();
		}
	}

	getPrice(): void {
		const sessionKey = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_session);
		this.serverDataService.getPrice(sessionKey).subscribe({
			next: this.shareService.setPriceDetails.bind(this.shareService),
			complete: () => {
				this.quotationLoading = false;
				this.navigateToQuotationPage();
			},
			error: (error) => {
				console.error(error);
				this.quotationLoading = false;
			},
		});
	}

	navigateToQuotationPage(): void {
		void this.router.navigate(['/quote'], { queryParamsHandling: 'merge' });
	}
}
